import React, { useState, useEffect, useRef } from 'react';
import { HashtagCollection } from '../../types/socialMediaPlanner';
import { SocialMediaPlannerService } from '../../services/socialMediaPlannerService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { SocialPlatform } from '../../types/socialMediaPlanner';

interface HashtagCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectHashtags?: (hashtags: string[]) => void;
  initialPlatform?: SocialPlatform;
}

const EMPTY_COLLECTION: HashtagCollection = {
  name: '',
  hashtags: [],
  description: ''
};

export const HashtagCollectionModal: React.FC<HashtagCollectionModalProps> = ({
  isOpen,
  onClose,
  onSelectHashtags,
  initialPlatform
}) => {
  const { user } = useAuth();
  const [collections, setCollections] = useState<HashtagCollection[]>([]);
  const [currentCollection, setCurrentCollection] = useState<HashtagCollection>(EMPTY_COLLECTION);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hashtagInput, setHashtagInput] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | undefined>(initialPlatform);

  const socialMediaPlannerService = new SocialMediaPlannerService();

  // Load collections when the modal opens
  useEffect(() => {
    if (isOpen) {
      loadCollections();
    }
  }, [isOpen]);
  
  // Create a ref for handling outside clicks
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const loadCollections = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const fetchedCollections = await socialMediaPlannerService.getHashtagCollections();
      setCollections(fetchedCollections);
    } catch (error) {
      console.error('Error loading hashtag collections:', error);
      toast.error('Failed to load hashtag collections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!currentCollection.name || currentCollection.hashtags.length === 0) {
      toast.error('Name and at least one hashtag are required');
      return;
    }

    setIsLoading(true);
    try {
      let updatedCollection;
      if (currentCollection.id) {
        // Update existing collection
        updatedCollection = await socialMediaPlannerService.updateHashtagCollection(currentCollection);
        toast.success('Hashtag collection updated successfully');
      } else {
        // Create new collection
        updatedCollection = await socialMediaPlannerService.createHashtagCollection({
          ...currentCollection,
          platform: selectedPlatform
        });
        toast.success('Hashtag collection created successfully');
      }
      
      await loadCollections();
      resetForm();
    } catch (error) {
      console.error('Error saving hashtag collection:', error);
      toast.error('Failed to save hashtag collection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (collectionId: string) => {
    if (window.confirm('Are you sure you want to delete this hashtag collection?')) {
      setIsLoading(true);
      try {
        await socialMediaPlannerService.deleteHashtagCollection(collectionId);
        toast.success('Hashtag collection deleted successfully');
        await loadCollections();
      } catch (error) {
        console.error('Error deleting hashtag collection:', error);
        toast.error('Failed to delete hashtag collection');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const editCollection = (collection: HashtagCollection) => {
    setCurrentCollection(collection);
    setIsEditing(true);
    setSelectedPlatform(collection.platform);
  };

  const resetForm = () => {
    setCurrentCollection(EMPTY_COLLECTION);
    setIsEditing(false);
    setHashtagInput('');
  };

  const addHashtag = () => {
    if (!hashtagInput.trim()) return;
    
    let tag = hashtagInput.trim();
    if (!tag.startsWith('#')) {
      tag = `#${tag}`;
    }
    
    if (!currentCollection.hashtags.includes(tag)) {
      setCurrentCollection({
        ...currentCollection,
        hashtags: [...currentCollection.hashtags, tag]
      });
    }
    
    setHashtagInput('');
  };

  const removeHashtag = (hashtag: string) => {
    setCurrentCollection({
      ...currentCollection,
      hashtags: currentCollection.hashtags.filter(tag => tag !== hashtag)
    });
  };

  const handleSelectCollection = (collection: HashtagCollection) => {
    if (onSelectHashtags) {
      onSelectHashtags(collection.hashtags);
      // Increment usage count
      socialMediaPlannerService.incrementHashtagCollectionUsage(collection.id as string)
        .catch(err => console.error('Error incrementing usage count:', err));
      onClose();
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:block sm:p-0">
        {/* Center modal contents */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div 
          ref={modalRef}
          className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <span className="text-blue-500 mr-2">#</span>
              Hashtag Collections
            </h3>
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <span className="text-xl">&times;</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left side - Collections list */}
            <div className="border-r pr-4">
              <h4 className="font-medium mb-2">Your Collections</h4>
              {isLoading && collections.length === 0 ? (
                <div className="py-4 text-center text-gray-500">Loading collections...</div>
              ) : collections.length === 0 ? (
                <div className="py-4 text-center text-gray-500">
                  No hashtag collections yet. Create your first one!
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {collections.map(collection => (
                    <div key={collection.id} className="border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium">{collection.name}</h5>
                          {collection.description && (
                            <p className="text-sm text-gray-500">{collection.description}</p>
                          )}
                          {collection.platform && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                              {collection.platform}
                            </span>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1">
                            {collection.hashtags.slice(0, 5).map((tag, i) => (
                              <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                {tag}
                              </span>
                            ))}
                            {collection.hashtags.length > 5 && (
                              <span className="text-xs text-gray-500">
                                +{collection.hashtags.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {onSelectHashtags && (
                            <button 
                              onClick={() => handleSelectCollection(collection)}
                              className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            >
                              Use
                            </button>
                          )}
                          <button
                            onClick={() => editCollection(collection)}
                            className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(collection.id as string)}
                            className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => {
                  resetForm();
                  setIsEditing(true);
                }}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <span className="mr-1">+</span>
                New Collection
              </button>
            </div>

            {/* Right side - Create/Edit form */}
            {isEditing && (
              <div>
                <h4 className="font-medium mb-3">
                  {currentCollection.id ? 'Edit Collection' : 'Create New Collection'}
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      value={currentCollection.name}
                      onChange={e => setCurrentCollection({...currentCollection, name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g., Marketing Hashtags"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <input
                      type="text"
                      value={currentCollection.description || ''}
                      onChange={e => setCurrentCollection({...currentCollection, description: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Optional description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Platform (Optional)</label>
                    <select
                      value={selectedPlatform || ''}
                      onChange={e => setSelectedPlatform(e.target.value as SocialPlatform)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">All platforms</option>
                      <option value="instagram">Instagram</option>
                      <option value="twitter">Twitter</option>
                      <option value="facebook">Facebook</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="pinterest">Pinterest</option>
                      <option value="tiktok">TikTok</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hashtags *</label>
                    <div className="mt-1 flex">
                      <input
                        type="text"
                        value={hashtagInput}
                        onChange={e => setHashtagInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addHashtag()}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Add a hashtag (e.g., #marketing)"
                      />
                      <button
                        type="button"
                        onClick={addHashtag}
                        className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {currentCollection.hashtags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeHashtag(tag)}
                            className="ml-1.5 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    {currentCollection.hashtags.length === 0 && (
                      <p className="mt-1 text-sm text-gray-500">
                        Add at least one hashtag
                      </p>
                    )}
                  </div>

                  <div className="pt-3 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateOrUpdate}
                      disabled={isLoading}
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isLoading ? 'Saving...' : currentCollection.id ? 'Update' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HashtagCollectionModal;
